import { useState, useEffect } from "react";
import ClientPublicLayout from "@/layouts/ClientPublicLayout";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import AuthDialog from "@/components/AuthDialog";
import BookingSummary from "@/components/BookingSummary";
import { getRequestOptions, logException } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Helmet } from "react-helmet";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import ClassCard from "@/components/ClassCard";
import { formatDate, formatTime, formatDay, setCurrentStudio } from '@/lib/utils';
import ResetPasswordDialog from "@/components/dialogs/ResetPasswordDialog";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import { useTranslation } from 'react-i18next';

const Studio = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { studioName } = useParams();
  const [studio, setStudio] = useState(null);
  const [pageTitle, setPageTitle] = useState('');
  const [searchParams] = useSearchParams();
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<typeof availableClasses[0] | null>(null);
  const [date, setDate] = useState<Date>();
  const [availableClasses, setAvailableClasses] = useState<any[]>([]);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordMode, setResetPasswordMode] = useState('');
  const [groupedClasses, setGroupedClasses] = useState<GroupedClasses>({});
  const [sortedDates, setSortedDates] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);


  const canonicalUrl =
    typeof window !== 'undefined'
      ? window.location.origin + window.location.pathname
      : 'https://fitcalendar.praxipro.io/studio/'; // fallback

  interface GroupedClasses {
    [key: string]: typeof availableClasses[0][]; // Key is a date string, value is an array of class items
  }

  const handleBooking = (classItem: typeof availableClasses[0]) => {
    setSelectedClass(classItem);
    if (user?.email) {
      setIsSummaryOpen(true);
    } else {
      setIsAuthOpen(true);
    }
  };

  const handleAuthSuccess = (email: string) => {
    setIsAuthOpen(false);
    setIsSummaryOpen(true);
    fetchClasses(studioName);
  };

  const handleConfirmBooking = (sessions: number) => {
    if (selectedClass) {
      fetchClasses(studioName);

      toast({
        title: t('classes.confirmBookingTitle'),
        description: t('classes.confirmBookingDescription')
          .replace('{title}', `${selectedClass.title}`)
          .replace('{trainer}', `${selectedClass.instructor}`)
          .replace('{date}', `${formatDate(selectedClass.classDateTime)}`)
          .replace('{time}', `${formatTime(selectedClass.classDateTime)}`)
          .replace('{sessions}', `${sessions}`),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
      setIsSummaryOpen(false);
    }
  };

  // const handleDateSelect = (selectedDate: Date | undefined) => {
  //   setDate(selectedDate);
  //   setIsCalendarOpen(false);
  // };

  // const handleBackToLogin = () => {
  //   setShowResetPassword(false);
  // };

  const checkStudioNameValidity = async (studioName: string) => {

    try {
      const response = await fetch(`${import.meta.env.VITE_BOOKING_STUDIO_ENDPOINT}` + '?url=' + studioName);
      if (!response.ok) {
        throw new Error("Studio not found");
      }
      const data = await response.json();
      setStudio(data);
      setPageTitle(data.name);
      setCurrentStudio(data);
      return true;
    } catch (error) {
      console.error("Error validating studio:", error);
      return false;
    }
  };

  const fetchClasses = async (studioName: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_AVAILABLE_CLASSES_ENDPOINT}` + '?url=' + studioName, getRequestOptions());

      if (!response.ok) {
        throw new Error('Failed to fetch classes');
      }

      const data = await response.json(); // Parse JSON data

      const filteredClasses = date
        ? data.filter((classItem) => {
          const classDate = new Date(classItem.date);
          return (
            classDate.getMonth() === date.getMonth() &&
            classDate.getFullYear() === date.getFullYear()
          );
        })
        : data;
      // Group classes by date
      const grouped: GroupedClasses = filteredClasses.reduce(
        (groups, classItem) => {
          const dateKey = new Date(classItem.classDateTime).toISOString().split("T")[0]; // Extract YYYY-MM-DD
          if (!groups[dateKey]) {
            groups[dateKey] = [];
          }
          groups[dateKey].push(classItem);
          return groups;
        },
        {} as GroupedClasses
      );

      // Sort dates
      const sorted = Object.keys(grouped).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      );
      // Update states
      setGroupedClasses(grouped);
      setSortedDates(sorted);
      setAvailableClasses(filteredClasses); // Store the retrieved classes in state
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const confirmRegistrationOrResetPassword = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const mode = searchParams.get('mode') || '';
    const token = searchParams.get('token') || '';

    if (mode === 'confirmregistration' && token !== '') {
      try {
        const response = await fetch(`${import.meta.env.VITE_CLIENT_CONFIRM_REGISTRATION_ENDPOINT}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          if (response.status === 409) {
            throw new Error("Email address is already registered");
          }
          throw new Error("Confirm Registration failed");
        }

        const data = await response.json();
        if (data) {
          setShowResetPassword(true);
          setResetPasswordMode(mode);
        }
      } catch (error) {
        console.error("Error during registration:", error);
        logException(error);
        if (error.message === "Email address is already registered") {
          alert(t("authDialog.emailAlreadyExists"));
        } else {
          alert(t("authDialog.registrationFailed"));
        }
      }
    }
    else if (mode === 'forgotpassword' && token !== '') {
      setShowResetPassword(true);
      setResetPasswordMode(mode);
    }
  };

  useEffect(() => {
    const studioQuery = searchParams.get("s");
    const studioToCheck = studioQuery || studioName;

    if (!studioToCheck) {
      // navigate("/"); // Redirect if no studio name or query is present
      return;
    }

    checkStudioNameValidity(studioToCheck).then((isValid) => {
      if (!isValid) {
        // navigate("/"); // Redirect if the studio is invalid
      } else {
        localStorage.setItem("studioURL", studioToCheck);
        fetchClasses(studioToCheck); // Proceed with fetching classes if valid
      }
    });
  }, [searchParams, studioName, navigate]);


  useEffect(() => {
    confirmRegistrationOrResetPassword();
  }, []);

  return (
    <ClientPublicLayout>
      <Helmet>
        <title>{pageTitle} | FitCalendar</title>
        <link rel="canonical" href={canonicalUrl} />

        <meta name="title" content={pageTitle} />
        <meta name="description" content="Manage your studio, with FitCalendar’s powerful booking and scheduling software." />
        <meta name="author" content="PraxiPro" />

        {/* Open Graph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content="Manage your studio, with FitCalendar’s powerful booking and scheduling software." />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:site_name" content="FitCalendar" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content="Manage your studio, with FitCalendar’s powerful booking and scheduling software." />
        <meta name="twitter:image" content="/og-image.png" />
        <meta name="twitter:creator" content="@PraxiPro" />

        {/* Schema.org microdata */}
        <meta itemProp="name" content={pageTitle} />
        <meta itemProp="description" content="Manage your studio, with FitCalendar’s powerful booking and scheduling software." />
        <meta itemProp="image" content="/og-image.png" />

        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          {studio?.name}
        </h2>
        <p className="text-lg text-gray-600 mb-6">
          {t('classes.bookIntro')}
        </p>

        {/* <div className="flex justify-center mb-8">
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <ButtonComponent
                variant="outline"
                className="w-[280px] justify-start text-left font-normal"
              >
                <Calendar className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : <span>{t('common.pickDate')}</span>}
              </ButtonComponent>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <CalendarComponent
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {date && (
            <ButtonComponent
              variant="ghost"
              className="ml-2"
              onClick={() => setDate(undefined)}
            >
              {t('common.clear')}
            </ButtonComponent>
          )}
        </div> */}
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        availableClasses.length === 0 ? (
          <Alert className="max-w-lg mx-auto mb-8">
            <AlertDescription>
              {date ? t('classes.noClassesAvailableClearFilter') : t('classes.noClassesAvailable')}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-8">
            {sortedDates.map((dateKey) => (
              <div key={dateKey} className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 py-2 px-4 bg-gray-50 rounded-lg border-l-4 border-red-500 shadow-md">
                  {formatDay(dateKey) + ' - ' + formatDate(dateKey)}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedClasses[dateKey].map((classItem) => (
                    <ClassCard
                      key={classItem.id}
                      classItem={classItem}
                      onBooking={() => handleBooking(classItem)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {selectedClass && (
        <>
          <BookingSummary
            isOpen={isSummaryOpen}
            onClose={() => setIsSummaryOpen(false)}
            classDetails={{
              id: selectedClass.id,
              title: selectedClass.title,
              classDateTime: selectedClass.classDateTime,
              duration: selectedClass.duration,
              instructor: selectedClass.instructor,
              spots: selectedClass.capacity,
              price: selectedClass.price,
              currency: selectedClass.currency,
              recurrent: selectedClass.recurrent,
              practiceId: selectedClass.practiceId,
            }}
            onConfirm={handleConfirmBooking}
          />
        </>
      )}
      <AuthDialog
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onSuccess={handleAuthSuccess}
      />
      <ResetPasswordDialog
        isOpen={showResetPassword}
        mode={resetPasswordMode}
        onClose={() => setShowResetPassword(false)}
      />
    </ClientPublicLayout>
  );
};

export default Studio;
